import type { ReelItem } from '~/components/PropertyReelsFeed/types';
import type { FeaturedVideoProperty } from '~/core/entities/property';
import type { FeaturedVideo } from '~/store/featuredVideosStore';

import { useLastVideoState } from '~/composables/useLastVideoState';
import { getOfflineViews } from '~/composables/usePropertyViewTracking';
import { useFeaturedVideosStore } from '~/store/featuredVideosStore';

interface VideoHistoryResponse {
	videos: FeaturedVideoProperty[];
	pagination: {
		page: number;
		perPage: number;
		total: number;
		totalPages: number;
	};
}

const HISTORY_PER_PAGE = 10;

interface FeaturedVideosModalState {
	isOpen: boolean;
	currentIndex: number;
	historyItems: ReelItem[];
	historyPage: number;
	hasMoreHistory: boolean;
	isLoadingHistory: boolean;
	isHistoryLoaded: boolean;
	isFeedLoaded: boolean;
}

function useModalState() {
	return useState<FeaturedVideosModalState>('featuredVideosModal', () => ({
		isOpen: false,
		currentIndex: 0,
		historyItems: [],
		historyPage: 1,
		hasMoreHistory: true,
		isLoadingHistory: false,
		isHistoryLoaded: false,
		isFeedLoaded: false
	}));
}

function propertyToReelItem(
	property: FeaturedVideoProperty,
	isFromHistory = false
): ReelItem | null {
	const featuredVideo = property.featuredVideos?.[0];
	if (!featuredVideo) return null;

	return {
		videoUrl: featuredVideo.url,
		thumbnailUrl: featuredVideo.thumbnail,
		property,
		videoType: 'native',
		videoView: property.videoView,
		isFromHistory,
		videoWidth: featuredVideo.width,
		videoHeight: featuredVideo.height
	};
}

function featuredVideoToReelItem(video: FeaturedVideo): ReelItem {
	return {
		videoUrl: video.videoUrl,
		thumbnailUrl: video.thumbnailUrl,
		property: video.property,
		videoType: 'native',
		videoView: video.property.videoView,
		isFromHistory: false,
		videoWidth: video.videoWidth,
		videoHeight: video.videoHeight
	};
}

function getOfflineHistoryAsReelItems(): ReelItem[] {
	return getOfflineViews()
		.filter(
			view =>
				view.videoData?.property &&
				(view.engagement?.completionRate ?? 0) >= 0.1
		)
		.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
		.map(view => {
			const videoData = view.videoData!;

			return {
				videoUrl: videoData.videoUrl,
				thumbnailUrl: videoData.thumbnailUrl,
				property: videoData.property!,
				videoType: 'native' as const,
				isFromHistory: true,
				videoView: {
					viewId: '',
					maxCompletionRate: view.engagement?.completionRate ?? 0,
					lastCompletionRate: view.engagement?.completionRate ?? 0,
					totalDuration: view.engagement?.duration ?? 0,
					viewCount: 1,
					lastViewedAt: view.timestamp
						? new Date(view.timestamp).toISOString()
						: new Date().toISOString()
				}
			};
		});
}

export function useFeaturedVideosModal() {
	const store = useFeaturedVideosStore();
	const { loggedIn } = useUserSession();
	const lastVideoState = useLastVideoState();

	const state = useModalState();

	const isOpen = computed({
		get: () => state.value.isOpen,
		set: (v: boolean) => {
			state.value.isOpen = v;
		}
	});
	const currentIndex = computed({
		get: () => state.value.currentIndex,
		set: (v: number) => {
			state.value.currentIndex = v;
		}
	});
	const historyItems = computed({
		get: () => state.value.historyItems,
		set: (v: ReelItem[]) => {
			state.value.historyItems = v;
		}
	});
	const historyPage = computed({
		get: () => state.value.historyPage,
		set: (v: number) => {
			state.value.historyPage = v;
		}
	});
	const hasMoreHistory = computed({
		get: () => state.value.hasMoreHistory,
		set: (v: boolean) => {
			state.value.hasMoreHistory = v;
		}
	});
	const isLoadingHistory = computed({
		get: () => state.value.isLoadingHistory,
		set: (v: boolean) => {
			state.value.isLoadingHistory = v;
		}
	});
	const isHistoryLoaded = computed({
		get: () => state.value.isHistoryLoaded,
		set: (v: boolean) => {
			state.value.isHistoryLoaded = v;
		}
	});
	const isFeedLoaded = computed({
		get: () => state.value.isFeedLoaded,
		set: (v: boolean) => {
			state.value.isFeedLoaded = v;
		}
	});

	const isLoggedIn = () => import.meta.client && loggedIn.value;

	watch(loggedIn, () => {
		historyItems.value = [];
		isHistoryLoaded.value = false;
		historyPage.value = 1;
		hasMoreHistory.value = true;
		currentIndex.value = 0;
		isFeedLoaded.value = false;
	});

	// Feed items from store (new videos)
	const feedItems = computed<ReelItem[]>(() => {
		return store.videos.map(featuredVideoToReelItem);
	});

	// Combined items: history first, then new videos (deduplicated)
	const reelItems = computed<ReelItem[]>(() => {
		const history = historyItems.value;
		const feed = feedItems.value;

		// Get IDs from history to avoid duplicates
		const historyIds = new Set(history.map(h => h.property.commercialId));

		// Filter feed to exclude items already in history
		const filteredFeed = feed.filter(
			f => !historyIds.has(f.property.commercialId)
		);

		// History comes first, then new videos
		return [...history, ...filteredFeed];
	});

	// Ready when both are loaded (for Embla initialization)
	// Also consider ready if store already has data (loaded elsewhere)
	const isReady = computed(() => {
		const historyReady = isHistoryLoaded.value || historyItems.value.length > 0;
		const feedReady = isFeedLoaded.value || store.isPreloaded;
		return historyReady && feedReady;
	});

	const isLoading = computed(() => store.isLoading || isLoadingHistory.value);

	const hasMore = computed(() => store.hasMore);
	const isPreloaded = computed(() => store.isPreloaded);

	// Number of history items (for calculating index offset)
	const historyCount = computed(() => historyItems.value.length);

	/**
	 * Load both history and feed simultaneously
	 */
	async function loadAll(): Promise<void> {
		if (!import.meta.client) return;

		// Start both requests simultaneously (they handle their own loading states)
		const historyPromise = loadHistory();
		const feedPromise = loadFeed();

		// Wait for both to complete
		await Promise.all([historyPromise, feedPromise]);
	}

	/**
	 * Load history (watched videos)
	 */
	async function loadHistory(): Promise<void> {
		// Already loaded
		if (isHistoryLoaded.value) return;

		// Already loading - wait for it to complete
		if (isLoadingHistory.value) {
			return new Promise<void>(resolve => {
				const stop = watch(
					() => isLoadingHistory.value,
					loading => {
						if (!loading) {
							stop();
							resolve();
						}
					},
					{ immediate: true }
				);
			});
		}

		isLoadingHistory.value = true;
		historyPage.value = 1;

		try {
			if (isLoggedIn()) {
				const result = await fetchHistoryVideos(1);
				historyItems.value = result.items;
				hasMoreHistory.value = result.hasMore;
			} else {
				// Use localStorage for offline users
				const offlineItems = getOfflineHistoryAsReelItems();
				historyItems.value = offlineItems;
				hasMoreHistory.value = false;
			}
			isHistoryLoaded.value = true;
		} finally {
			isLoadingHistory.value = false;
		}
	}

	/**
	 * Load feed (new videos)
	 */
	async function loadFeed(): Promise<void> {
		// Already loaded
		if (isFeedLoaded.value) return;

		// Already loading - wait for it to complete
		if (store.isLoading) {
			return new Promise<void>(resolve => {
				const stop = watch(
					() => store.isLoading,
					loading => {
						if (!loading) {
							stop();
							isFeedLoaded.value = true;
							resolve();
						}
					},
					{ immediate: true }
				);
			});
		}

		// Already preloaded by store
		if (store.isPreloaded) {
			isFeedLoaded.value = true;
			return;
		}

		await store.preload();
		isFeedLoaded.value = true;
	}

	/**
	 * Fetch history videos from API
	 */
	async function fetchHistoryVideos(
		page: number
	): Promise<{ items: ReelItem[]; hasMore: boolean }> {
		try {
			const response = await $fetch<VideoHistoryResponse>(
				'/api/property-views/video-history',
				{ query: { page, perPage: HISTORY_PER_PAGE } }
			);

			const items = (response.videos ?? [])
				.map(p => propertyToReelItem(p, true))
				.filter((item): item is ReelItem => item !== null);

			const pagination = response.pagination;
			const hasMorePages =
				(pagination?.page ?? 1) < (pagination?.totalPages ?? 1);

			return { items, hasMore: hasMorePages };
		} catch {
			return { items: [], hasMore: false };
		}
	}

	/**
	 * Load more history items (when scrolling up at the beginning)
	 */
	async function loadMoreHistory(): Promise<void> {
		if (isLoadingHistory.value || !hasMoreHistory.value || !isLoggedIn()) {
			return;
		}

		isLoadingHistory.value = true;

		try {
			const nextPage = historyPage.value + 1;
			const result = await fetchHistoryVideos(nextPage);

			if (result.items.length > 0) {
				// Prepend new history items at the beginning
				historyItems.value = [...result.items, ...historyItems.value];
				hasMoreHistory.value = result.hasMore;
				historyPage.value = nextPage;

				// Adjust current index to account for new items
				currentIndex.value += result.items.length;
			} else {
				hasMoreHistory.value = false;
			}
		} finally {
			isLoadingHistory.value = false;
		}
	}

	/**
	 * Load more feed items (when scrolling down at the end)
	 */
	async function loadMoreFeed(): Promise<void> {
		await store.loadMore();
	}

	/**
	 * Preload for background loading
	 */
	async function preload(): Promise<void> {
		if (store.isPreloaded || store.isLoading || !import.meta.client) return;
		await store.preload();
		isFeedLoaded.value = true;
	}

	async function open(initialIndex?: number): Promise<void> {
		if (isLoading.value) return;

		await loadAll();

		// If no initial index provided, try to restore last position
		if (initialIndex === undefined) {
			const restoredIndex = restoreLastPosition();
			currentIndex.value =
				restoredIndex !== null ? restoredIndex : historyCount.value;
		} else {
			currentIndex.value = initialIndex;
		}

		isOpen.value = true;
	}

	function close(): void {
		isOpen.value = false;
	}

	async function openByPropertyId(propertyId: string): Promise<void> {
		await loadAll();

		const index = reelItems.value.findIndex(
			v => v.property.commercialId === propertyId
		);

		currentIndex.value = index >= 0 ? index : historyCount.value;
		isOpen.value = true;
	}

	/**
	 * Navigate to /shorts page
	 */
	function navigateToShorts(query?: Record<string, string>): void {
		const router = useRouter();
		router.push({ path: '/shorts', query });
	}

	/**
	 * Navigate to video page by property ID
	 */
	function navigateToVideo(propertyId: string): void {
		const router = useRouter();
		router.push(`/video/${propertyId}`);
	}

	function resetState(): void {
		currentIndex.value = 0;
		historyItems.value = [];
		historyPage.value = 1;
		hasMoreHistory.value = true;
		isHistoryLoaded.value = false;
		isFeedLoaded.value = false;
	}

	/**
	 * Save current video position to localStorage
	 */
	function saveCurrentPosition(): void {
		const items = reelItems.value;
		const index = currentIndex.value;
		const item = items[index];

		if (item) {
			lastVideoState.save(item.property.commercialId, index);
		}
	}

	/**
	 * Find video index by property ID
	 */
	function findVideoIndexByPropertyId(propertyId: string): number {
		return reelItems.value.findIndex(
			item => item.property.commercialId === propertyId
		);
	}

	/**
	 * Restore last video position from localStorage
	 * Returns the index of the last watched video, or null if not found
	 */
	function restoreLastPosition(): number | null {
		const lastState = lastVideoState.get();
		if (!lastState) return null;

		// First, try to find the video by propertyId
		const foundIndex = findVideoIndexByPropertyId(lastState.propertyId);
		if (foundIndex >= 0) {
			return foundIndex;
		}

		// Fallback: use the saved index if it's valid
		// This handles cases where the video is in a page not yet loaded
		if (lastState.index >= 0 && lastState.index < reelItems.value.length) {
			return lastState.index;
		}

		// If saved index is beyond current items but we have more pages,
		// return the last available index (user can continue from there)
		if (
			lastState.index >= reelItems.value.length &&
			reelItems.value.length > 0
		) {
			return reelItems.value.length - 1;
		}

		return null;
	}

	/**
	 * Get the starting index for new videos (after history)
	 */
	function getNewVideosStartIndex(): number {
		return historyCount.value;
	}

	return {
		isOpen,
		reelItems,
		feedItems,
		historyItems: readonly(historyItems),
		isLoading,
		isReady,
		hasMore,
		hasMoreHistory: readonly(hasMoreHistory),
		currentIndex,
		isPreloaded,
		isHistoryLoaded: readonly(isHistoryLoaded),
		historyCount,
		isLoadingHistory: readonly(isLoadingHistory),
		open,
		openByPropertyId,
		close,
		loadAll,
		loadHistory,
		loadMoreFeed,
		loadMoreHistory,
		preload,
		resetState,
		navigateToShorts,
		navigateToVideo,
		saveCurrentPosition,
		restoreLastPosition,
		getNewVideosStartIndex
	};
}
